import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
// Route to translation key mapping
const routeToLabelMap = {
    '/': 'common.home',
    '/people': 'common.people',
    '/people/:id': 'people.personDetails',
    '/relations': 'relations.relationsManagement',
    '/relations/:id': 'relations.relationDetails',
    '/events': 'common.events',
    '/event-details/:eventId': 'events.createNewEvent.eventDetails',
    '/event-details/:eventId/:type': 'common.events',
    '/create-event': 'events.createEvent',
    '/tasks': 'common.tasks',
    '/event-tasks': 'tasks.eventTasks',
    '/event-tasks/:eventId': 'tasks.eventTasks',
    '/users': 'common.users',
    '/collaborators': 'common.collaborators',
    '/roles-permissions': 'common.rolesPermissions',
    '/roles-permissions/new': 'rolesPermissions.addRole',
    '/roles-permissions/:roleId/permissions': 'rolesPermissions.permissions',
    '/sponsorships': 'common.sponsorships',
    '/sponsorships/new-sponsorship': 'sponsorships.newSponsorship',
    '/sponsorship-details/:id': 'sponsorships.sponsorshipDetails',
    '/sponsorship-details/:id/ticket-detail/:eventId': 'sponsorships.ticketDetails',
    '/settings': 'common.settings',
    '/global-search': 'common.search'
};
// Map detail routes to their parent routes
const detailToParentMap = {
    '/sponsorship-details': '/sponsorships',
    '/people': '/people',
    '/relations': '/relations'
};
// Helper function to match dynamic routes
const matchRoute = (pathname) => {
    // Try exact match first
    if (routeToLabelMap[pathname]) {
        return routeToLabelMap[pathname];
    }
    // Try to match dynamic routes
    const pathParts = pathname.split('/').filter(Boolean);
    const routes = Object.keys(routeToLabelMap);
    for (const route of routes) {
        const routeParts = route.split('/').filter(Boolean);
        if (routeParts.length !== pathParts.length)
            continue;
        const matches = routeParts.every((part, index) => {
            return part.startsWith(':') || part === pathParts[index];
        });
        if (matches) {
            return routeToLabelMap[route];
        }
    }
    return null;
};
// Generate breadcrumbs from current path
const generateBreadcrumbs = (pathname, t) => {
    const breadcrumbs = [];
    const pathParts = pathname.split('/').filter(Boolean);
    // Build breadcrumbs by accumulating path segments
    let currentPath = '';
    for (let i = 0; i < pathParts.length; i++) {
        let part = pathParts[i];
        // Skip query parameters
        if (part.includes('?')) {
            part = part.split('?')[0];
        }
        currentPath += `/${part}`;
        // Check if this is an ID (numeric or GUID)
        const isId = /^\d+$/.test(part) || /^[a-f0-9-]{36}$/i.test(part);
        if (isId) {
            // Add parent breadcrumb first if not already added
            const detailPath = currentPath.replace(/\/[^/]+$/, '');
            // Check if this detail path has a mapped parent (e.g., /sponsorship-details -> /sponsorships)
            const actualParentPath = detailToParentMap[detailPath] || detailPath;
            if (actualParentPath && !breadcrumbs.find((b) => b.path === actualParentPath)) {
                const parentRoute = matchRoute(actualParentPath);
                if (parentRoute) {
                    breadcrumbs.push({
                        label: t(parentRoute),
                        path: actualParentPath
                    });
                }
            }
            // Normalize the detail path by replacing all IDs with parameter placeholders
            const normalizedDetailPath = detailPath.replace(/\/[a-f0-9-]{36}|\d+/gi, (match, offset) => {
                // Determine parameter name based on position in path
                if (detailPath.includes('ticket-detail')) {
                    return '/:id'; // First ID in paths with ticket-detail
                }
                return '/:id';
            });
            // Try different parameter names for the current ID
            const paramNames = ['/:id', '/:eventId'];
            let matchedRoute = null;
            for (const paramName of paramNames) {
                const dynamicRoute = normalizedDetailPath + paramName;
                matchedRoute = matchRoute(dynamicRoute);
                if (matchedRoute) {
                    breadcrumbs.push({
                        label: t(matchedRoute),
                        path: currentPath
                    });
                    break;
                }
            }
            continue;
        }
        // For non-numeric parts, try to match the current path
        const matchedRoute = matchRoute(currentPath);
        if (matchedRoute) {
            // Skip adding this breadcrumb if it's a detail route that will have an ID following it
            // and we should use its parent instead
            const nextPart = pathParts[i + 1];
            const nextIsId = nextPart && (/^\d+$/.test(nextPart) || /^[a-f0-9-]{36}$/i.test(nextPart));
            const hasParentMapping = detailToParentMap[currentPath];
            if (nextIsId && hasParentMapping) {
                // Skip this breadcrumb, it will be added via parent mapping when processing the ID
                continue;
            }
            // Also skip if this is an intermediate path in a nested dynamic route
            // e.g., skip /sponsorship-details/id/ticket-detail when the full path is /sponsorship-details/id/ticket-detail/eventId
            if (nextIsId && currentPath.includes('/')) {
                // Normalize the current path by replacing any IDs with :id
                const normalizedPath = currentPath.replace(/\/([a-f0-9-]{36}|\d+)/gi, '/:id');
                // Check if adding :eventId would match a longer route pattern
                const testRoute = normalizedPath + '/:eventId';
                if (routeToLabelMap[testRoute]) {
                    // This is an intermediate path, skip it
                    continue;
                }
            }
            breadcrumbs.push({
                label: t(matchedRoute),
                path: currentPath
            });
        }
        else if (part !== 'new') {
            // Check if this is a detail path that should be skipped
            const nextPart = pathParts[i + 1];
            const nextIsId = nextPart && (/^\d+$/.test(nextPart) || /^[a-f0-9-]{36}$/i.test(nextPart));
            const hasParentMapping = detailToParentMap[currentPath];
            if (nextIsId && hasParentMapping) {
                // Skip this breadcrumb, it will be added when processing the ID
                continue;
            }
            // Fallback: use the path part as label (capitalized)
            const label = part
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            breadcrumbs.push({
                label,
                path: currentPath
            });
        }
    }
    return breadcrumbs;
};
const Breadcrumbs = ({ customBreadcrumbs }) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const breadcrumbs = customBreadcrumbs || generateBreadcrumbs(location.pathname, t);
    // Don't show breadcrumbs if there's only one item (home page)
    if (breadcrumbs.length <= 1) {
        return null;
    }
    return (_jsx(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            flexWrap: 'wrap'
        }, children: breadcrumbs.map((item, index) => {
            const isActive = index === breadcrumbs.length - 1;
            return (_jsxs(React.Fragment, { children: [index > 0 && (_jsx(Icon, { icon: 'eva:arrow-ios-back-outline', width: 16, height: 16, style: {
                            color: theme.palette.grey[500],
                            margin: `0 ${theme.spacing(0.5)}`
                        } })), _jsx(Typography, { variant: 'body2', onClick: () => {
                            if (isActive)
                                return;
                            if (item.onClick) {
                                item.onClick();
                                return;
                            }
                            if (item.path) {
                                navigate(item.path);
                            }
                        }, sx: {
                            color: isActive ? theme.palette.grey[400] : theme.palette.common.white,
                            fontWeight: 400,
                            cursor: !isActive && (item.path || item.onClick) ? 'pointer' : 'default',
                            '&:hover': {
                                textDecoration: !isActive && (item.path || item.onClick) ? 'underline' : 'none'
                            }
                        }, children: item.label })] }, item.path || index));
        }) }));
};
export default Breadcrumbs;
